import { useEffect, useRef, useState } from "react";

import { FieldGroup, Input } from "./Field";

export interface NumericInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

export const NumericInput = ({
  value,
  onChange,
  min = -999,
  max = 999,
  step = 1,
  className,
}: NumericInputProps) => {
  // The field is driven by local text, not directly by `value`. A native
  // number input's raw text ("", "-", "12.") often doesn't round-trip
  // through parseFloat back to the same string, and a controlled input's
  // DOM value gets reverted to the last committed `value` prop whenever its
  // onChange handler doesn't report a new one for that keystroke (e.g. a
  // momentarily empty/partial value) — so anchoring the field straight to
  // `value` fights every backspace and select-all-to-retype. Tracking what
  // was typed locally and echoing it straight back means React never has
  // anything to revert; a parsed number is only bubbled up to the consumer
  // once the text is a valid finite number.
  const [text, setText] = useState(String(value));
  const isFocusedRef = useRef(false);

  // Stay in sync with external changes (e.g. a paired range slider), but
  // don't stomp on the user's in-progress edit while they're typing.
  useEffect(() => {
    if (!isFocusedRef.current) setText(String(value));
  }, [value]);

  return (
    <FieldGroup size="small" className={className}>
      <Input
        type="number"
        value={text}
        min={min}
        max={max}
        step={step}
        onFocus={() => {
          isFocusedRef.current = true;
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          // Snap back to the last valid committed value if the field was
          // left in a non-numeric state (empty, a lone "-", etc.).
          setText(String(value));
        }}
        onChange={(e) => {
          const raw = (e.target as HTMLInputElement).value;
          setText(raw);
          const parsed = parseFloat(raw);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
      />
    </FieldGroup>
  );
};
